// Header
import Avatar from 'components/common/Avatar';
import Button from 'components/common/Button';
import Icon from 'components/common/Icon';
import TabBar, { TabBarProps } from 'components/common/TabBar';
import useToast from 'components/common/Toast';
import ROUTES from 'config/routes';
import React, { PropsWithChildren } from 'react';
import { trackEvent } from 'services/analytics';
import { CreatorProfile } from 'types/profile';
import Rating from 'components/common/Rating';
import styles from './Header.module.sass';

export interface HeaderProps<T extends string>
  extends Omit<TabBarProps<T>, 'className'>,
    Pick<
      CreatorProfile,
      | 'username'
      | 'creatorId'
      | 'firstName'
      | 'lastName'
      | 'profilePicture'
      | 'address'
      | 'ageRange'
      | 'overallRating'
    > {}

const Header = <P extends string>({
  tabs,
  username,
  creatorId,
  overallRating,
  firstName,
  lastName,
  profilePicture,
  address: { city, state },
  ageRange,
  selected,
  onTabSelect,
}: PropsWithChildren<HeaderProps<P>>): JSX.Element => {
  const { triggerToast } = useToast();

  const copyPublicLink = () => {
    const profileLink =
      window.location.origin +
      ROUTES.PublicProfile.getPath(username || creatorId);

    navigator.clipboard.writeText(profileLink);
    triggerToast('Link Copied to Clipboard');
    trackEvent('Creator Copies Portfolio Link', { profileLink });
  };

  const trackEditProfileClick = () => {
    // ???: Should we add trackEvent to Common/Button?
    trackEvent('Creator Clicks Edit Profile Button');
  };

  return (
    <div className={styles.scrollContainer}>
      <div className={styles.tabContainer}>
        <div className={styles.header}>
          <div className={styles.profileContainer}>
            {/* Picture & Personal Info */}
            <Avatar
              type="square"
              className={styles.profilePicture}
              src={profilePicture}
            />
            <div className={styles.textColumn}>
              <div className={styles.nameRow}>
                <h4>
                  {firstName} {lastName}
                </h4>
              </div>
              <p>
                {city}, {state} | Age {ageRange}
              </p>
              <Rating rating={overallRating || 5} />
            </div>
          </div>
          <div className={styles.actionContainer}>
            {/* EditProfile & View Public Page */}
            <div className={styles.buttonRow}>
              <Button
                href={ROUTES.AccountSettings.getPath()}
                internalLink
                fullWidth={false}
                type="outline"
                onClick={trackEditProfileClick}
              >
                Edit Profile
              </Button>
              <Button fullWidth={false} type="primary" onClick={copyPublicLink}>
                Share Public Page
                <Icon name="link" className={styles.linkIcon} />
              </Button>
            </div>
            <p>
              Share your portfolio with brands you want to work with and add it
              to your social bios
            </p>
          </div>
        </div>
        <TabBar
          className={styles.tabBar}
          tabs={tabs}
          selected={selected}
          onTabSelect={onTabSelect}
        />
      </div>
    </div>
  );
};

export default Header;
